import type { Summary } from '../../api/types'
import type { BusinessTime } from '../../time/businessTime'
import { Skeleton } from '../../ui'
import { useT } from '../../i18n'
import styles from './transactions.module.css'

export interface SummaryStripProps {
  readonly summary: Summary | undefined
  readonly businessTime: BusinessTime
  readonly loading: boolean
}

/**
 * Four numbers a biller checks before reading a single row. `filtered_*` is
 * shown rather than `total_*` because the question being answered is "what
 * does the view in front of me add up to" — a strip that ignores the active
 * filter invites reconciling against the wrong figure.
 */
export function SummaryStrip({ summary, businessTime, loading }: SummaryStripProps) {
  const t = useT()

  if (loading && !summary) {
    return (
      <div className={styles.summary}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={styles.card}>
            <Skeleton variant="row" label={t('tx.summaryLoading')} />
          </div>
        ))}
      </div>
    )
  }
  if (!summary) return null

  const cards = [
    {
      label: t('tx.summaryFiltered'),
      value: businessTime.number(summary.filtered_count),
      sub: t('tx.summaryOfTotal', { total: businessTime.number(summary.total_count) }),
    },
    { label: t('tx.summaryValue'), value: businessTime.money(summary.filtered_amount_xaf), sub: '' },
    // No pending/reversed sub-lines: neither status is imported, so both are
    // permanently zero and a zero on a reconciliation strip reads as a fact
    // about the data rather than about the pipeline.
    { label: t('tx.summarySuccess'), value: businessTime.number(summary.success_count), sub: '' },
    { label: t('tx.summaryFailed'), value: businessTime.number(summary.failed_count), sub: '' },
  ]

  return (
    <div className={styles.summary}>
      {cards.map((card) => (
        <div key={card.label} className={styles.card}>
          <p className={styles.cardLabel}>{card.label}</p>
          <p className={styles.cardValue}>{card.value}</p>
          {card.sub && <p className={styles.cardSub}>{card.sub}</p>}
        </div>
      ))}
    </div>
  )
}
